import { Component, EventEmitter, Inject, Input, OnInit } from '@angular/core';
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import moment from 'moment';

import { MAT_DIALOG_DATA } from '@angular/material/dialog';
import { ServiceLevelDisplayNames } from '@pdqdev/pdq-shared/dist/CarrierData';
import { CountryCode, getCountryDisplayNameByCountryCode } from '@pdqdev/pdq-shared/dist/utils/Country-utils';
import { RulesActions, RulesConditions, Step } from '@app/features/automation-rules/utils';
import { capitalize, currencyStrToSign } from 'src/utils';
import { first } from 'rxjs/operators';
import { Shop } from '@app/services/shops/shop.model';
import { ShopService } from '@app/services/shops/shop.service';
import { CarrierOptions, ProviderOptions } from '@app/features/side-panel/utils/side-panel-dto';
import { SidePanelService } from '@app/features/side-panel/side-panel.service';
import { InsuranceAmountTypeSign } from '@app/pages/checkout-app/types';

interface DialogData {
  step: Step;
}

export interface RuleItem {
  id: number;
  enabled: boolean;
  conditionNames: string[][];
  conditionText: string[][];
  conditionValues: any;
  actionName: string;
  actionText: string;
  actionValue: any;
  updatedAt: string;
  internalName: string;
  params: {
    orWhen: Array<{
      andWhen: Array<any>;
    }>;
    andThen: Array<any>;
  };
}

@Component({
  selector: 'rules-list',
  templateUrl: './rules-list.component.html',
  styleUrls: ['./rules-list.component.scss'],
  standalone: false,
})
export class RulesListComponent implements OnInit {
  step: Step;

  @Input() showLoader: boolean = false;
  @Input() items: Array<RuleItem> = [];
  @Input() notVerifiedName: string;
  @Input() parameters: any;

  onChangeRulePriority: EventEmitter<any> = new EventEmitter();
  onRuleToggle: EventEmitter<any> = new EventEmitter();
  onEditRule: EventEmitter<any> = new EventEmitter();
  onDuplicateRule: EventEmitter<any> = new EventEmitter();
  onDeleteRule: EventEmitter<any> = new EventEmitter();
  shopSettings: any;
  carrierOptions: CarrierOptions[];
  isGetCarrierOptions;
  readonly RulesActions = RulesActions;

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: DialogData,
    private shopService: ShopService,
    private sidePanelService: SidePanelService,
  ) {}

  ngOnInit() {
    this.initDialogData();
    this.initShopSettings();
    this.initCarrierOptions();
  }

  initDialogData() {
    if (!this.data) {
      return;
    }
    this.step = this.data.step;
  }

  initShopSettings() {
    this.shopService.shop$.pipe(first()).subscribe((shop: Shop) => {
      this.shopSettings = {
        currency: currencyStrToSign(shop.metadata?.currency),
        weightUnits: shop.metadata?.weightUnits || 'lb',
      };
    });
  }

  private async initCarrierOptions() {
    if (this.isGetCarrierOptions) {
      return;
    }

    this.isGetCarrierOptions = true;

    try {
      // try to get data from parameters
      const parameterData = this.getCarrierOptionsFromParameters();
      if (parameterData) {
        this.carrierOptions = parameterData;
        return;
      }

      // If not available in parameters, fetch from API
      const data = await this.sidePanelService.getShopCarriers().pipe(first()).toPromise();
      this.carrierOptions = data.reduce(
        (res, carrier) =>
          res.concat(
            !carrier.providers?.length
              ? [new CarrierOptions(carrier)]
              : carrier.providers?.map((provider) => new ProviderOptions(provider, carrier)),
          ),
        [],
      );
    } catch (error) {
      console.error('Error fetching carrier options:', error);
      this.carrierOptions = [];
    }
  }

  private getCarrierOptionsFromParameters(): CarrierOptions[] | null {
    if (!this.parameters?.actions?.[0]?.values) {
      return null;
    }

    // Check for setRecommendedCarrier action data
    const setRecommendedCarrierData = this.parameters.actions[0].values.find(
      (action) => action.name === 'setRecommendedCarrier',
    )?.values;

    // Check for SmartShipOn action data
    const smartShipOnData = this.parameters.actions[0].values.find(
      (action) => action.name === RulesActions.SmartShipOn,
    )?.values;

    const data = setRecommendedCarrierData || smartShipOnData;

    if (!data) {
      return null;
    }

    return data.reduce(
      (res, carrier) =>
        res.concat(
          !carrier.providers?.length
            ? [new CarrierOptions(carrier)]
            : carrier.providers?.map((provider) => new ProviderOptions(provider, carrier)),
        ),
      [],
    );
  }

  async onDroped(event: CdkDragDrop<string[]>) {
    if (event.previousIndex === event.currentIndex) {
      return;
    }

    moveItemInArray(this.items, event.previousIndex, event.currentIndex);

    const indexesChanged = Math.max(event.previousIndex, event.currentIndex);

    for (let i = 0; i <= indexesChanged; i++) {
      // Small hack to add delay between emit events
      await new Promise((resolve) =>
        setTimeout(() => {
          this.onChangeRulePriority.emit({
            id: this.items[i].id,
            priority: i + 1,
          });
          resolve(true);
        }, 100),
      );
    }
  }

  onRuleClick(event: MouseEvent) {
    const ruleId: number = event.target['id'];

    if (!ruleId) {
      return;
    }

    const selectedRule = this.items.find((rule) => rule.id == ruleId);
    this.onEditRule.emit(selectedRule);
  }

  onRuleToggleHandler(checked, ruleId: number): void {
    this.onRuleToggle.emit({
      id: ruleId,
      enabled: checked,
    });
  }

  onEditRuleHandler(ruleId: number): void {
    const selectedRule = this.items.find((rule) => rule.id == ruleId);
    this.onEditRule.emit(selectedRule);
  }

  onDuplicateRuleHandler(ruleId: number): void {
    const selectedRule = this.items.find((rule) => rule.id == ruleId);
    this.onDuplicateRule.emit(selectedRule);
  }

  onDeleteRuleHandler(ruleId: number): void {
    this.onDeleteRule.emit({
      id: ruleId,
    });
  }

  getTimeFromNow(date) {
    return moment(date).fromNow();
  }

  getCountryNameByCode(code: string) {
    if (!code) {
      return;
    }
    return getCountryDisplayNameByCountryCode(code as CountryCode);
  }

  getConditionTitle() {
    let title = '';
    switch (this.step) {
      case Step.verify:
        title = 'For new orders';
        break;
      case Step.assign:
        title = 'For orders that are ready to be assigned';
        break;
      case Step.fulfill:
        title = 'For orders that are appeared in the fulfill screen';
        break;
      case Step.track:
        title = 'For orders that are appeared in the track screen';
        break;
    }
    return title;
  }

  getConditionText(item: RuleItem) {
    let text = '';
    const orWhenLength = item.conditionNames?.length;
    for (let i = 0; i < orWhenLength; i++) {
      for (let j = 0; j < item.conditionNames[i].length; j++) {
        const namesLength = item.conditionNames[i].length;
        let currentText = `<span class="value">${item.conditionText[i][j]}</span>`;
        switch (item.conditionNames[i][j]) {
          case 'deliveryMethod':
            const conditionValue = item.conditionValues[i][j];
            const afterOperatorText = (() => {
              switch (conditionValue.operator?.value) {
                case 'is':
                  return 'any of';
                case 'is-not':
                  return 'any of';
                case 'contains':
                  return 'any of';
                case 'not-contains':
                  return 'all of';
                default:
                  return 'any of';
              }
            })();
            const conditionValueTxt =
              typeof conditionValue === 'string'
                ? conditionValue
                : `${item.conditionValues[i][j].operator?.label} ${afterOperatorText} ${item.conditionValues[i][
                    j
                  ].values
                    .map((val) => val.label)
                    .join(', ')}`;
            currentText = `${item.conditionText[i][j]} <span class="value">${conditionValueTxt}</span>`;
            break;
          case 'shippingAddressCountry':
            currentText = `${item.conditionText[i][j]} <span class="value">${
              item.conditionValues[i][j]?.is ? 'is' : 'is not'
            } ${item.conditionValues[i][j]?.countries
              ?.map((country) => this.getCountryNameByCode(country))
              .join(' or ')}</span>`;
            break;
          case 'shippingAddressState':
            currentText = `${item.conditionText[i][j]} <span class="value">${
              item.conditionValues[i][j]?.operator.label
            } ${item.conditionValues[i][j]?.states?.join(' or ')}</span>`;
            break;
          case 'totalOrderWeight':
          case 'itemQuantity':
            currentText = `${item.conditionText[i][j]} <span class="value">${item.conditionValues[i][j]?.operator?.label} ${item.conditionValues[i][j]?.amount}</span>`;
            break;
          case 'totalOrderValue':
            currentText = `${item.conditionText[i][j]} is <span class="value">${item.conditionValues[i][j]?.operator?.label} ${item.conditionValues[i][j]?.amount}${this.shopSettings?.currency}</span>`;
            break;
          case 'inventoryLocation':
            currentText = `${item.conditionText[i][j]} <span class="value">${item.conditionValues[i][j]?.operator?.label} ${item.conditionValues[i][j]?.address?.label}</span>`;
            break;
          case 'boxChosen':
            currentText = `${item.conditionText[i][j]} <span class="value">${
              item.conditionValues[i][j]?.operator?.label
            } ${item.conditionValues[i][j]?.boxesArray?.map((box: any) => box.label).join(' or ')}</span>`;
            break;
          case 'isFilteredBy':
          case 'isNotFilteredBy':
            currentText = `${item.conditionText[i][j]} ${item.conditionValues[i][j]?.filtersArray
              ?.map((filter: any) => `<span class="value"> ${filter.label} </span>`)
              .join(' or ')}`;
            break;
          case 'orderStatusFix':
            currentText = `${item.conditionText[i][j]} ${item.conditionValues[i][j]?.statusArray
              ?.map((filter: any) => `<span class="value"> ${filter.label} </span>`)
              .join(' or ')}`;
            break;
          case RulesConditions.PaymentStatus:
            currentText = `${item.conditionText[i][j]} ${item.conditionValues[i][j]?.statuses
              ?.map((filter: any) => `<span class="value"> ${filter.label} </span>`)
              .join(' or ')}`;
            break;
          case RulesConditions.OrderTag:
            if (['contains', 'notContains'].includes(item.conditionValues[i][j].operator.value)) {
              currentText = `${item.conditionText[i][j]}
          <span class="value"> ${item.conditionValues[i][j]?.operator?.label} ${item.conditionValues[i][j].operator2?.label} ${item.conditionValues[i][j].value} </span>`;
            } else {
              const selectedOrderTags = item.conditionValues[i][j]?.selectedTags;
              const splitOrderTagText = item.conditionText[i][j].split(' ');
              const validOrderTagName = splitOrderTagText?.length == 2;
              currentText = `${
                validOrderTagName ? `${splitOrderTagText[0]}` : item.conditionText[i][j]
              } <span class="value">${item.conditionValues[i][j]?.operator?.label} ${
                item.conditionValues[i][j].operator2?.label
              } </span> ${validOrderTagName ? `${splitOrderTagText[1]} ` : item.conditionText[i][j]} ${selectedOrderTags
                ?.map((selectedTag: any) => `<span class="value"> ${selectedTag.name} </span>`)
                .join(
                  item.conditionValues[i][j]?.operator2?.value == 'allOf'
                    ? ' and '
                    : item.conditionValues[i][j]?.operator2?.value == 'anyOf'
                      ? ' or '
                      : ' and ',
                )}`;
            }
            break;
          case RulesConditions.OrderSKU:
            if (['contains', 'notContains'].includes(item.conditionValues[i][j].operator.value)) {
              currentText = `${item.conditionText[i][j]}
          <span class="value"> ${item.conditionValues[i][j]?.operator?.label} ${item.conditionValues[i][j].operator2?.label} ${item.conditionValues[i][j].value} </span>`;
            } else {
              const selectedOrderSkus = item.conditionValues[i][j]?.selectedSkus;
              const splitOrderSkuText = item.conditionText[i][j].split(' ');
              const validOrderSkuName = splitOrderSkuText?.length == 2;
              currentText = `${
                validOrderSkuName ? `${splitOrderSkuText[0]}` : item.conditionText[i][j]
              } <span class="value">${item.conditionValues[i][j]?.operator?.label} ${
                item.conditionValues[i][j].operator2?.label
              } </span> ${validOrderSkuName ? `${splitOrderSkuText[1]} ` : item.conditionText[i][j]} ${selectedOrderSkus
                ?.map((selectedSku: any) => `<span class="value"> ${selectedSku} </span>`)
                .join(
                  item.conditionValues[i][j]?.operator2?.value == 'allOf'
                    ? ' and '
                    : item.conditionValues[i][j]?.operator2?.value == 'anyOf'
                      ? ' or '
                      : ' and ',
                )}`;
            }
            break;
          case RulesConditions.CreatedOn:
            currentText = `${item.conditionText[i][j]} <span class="value">${
              !item.conditionValues[i][j]?.weekDays.length || item.conditionValues[i][j]?.weekDays.length === 7
                ? 'all week days'
                : item.conditionValues[i][j]?.weekDays
                    .slice()
                    .sort((a, b) => a.value - b.value)
                    .map((day) => day.label)
                    .join(', ')
            }</span> from <span class="value">${item.conditionValues[i][j]?.from.value}</span> to <span class="value">${
              item.conditionValues[i][j]?.to.value
            }</span>`;
            break;
          case RulesConditions.ShipOnDate:
            currentText = `${item.conditionText[i][j]} <span class="value">${item.conditionValues[i][j]?.operator?.label} 
            ${
              item.conditionValues[i][j]?.value?.label !== 'in X days'
                ? item.conditionValues[i][j]?.value?.label
                : 'in ' +
                  item.conditionValues[i][j]?.value?.dayNumber +
                  ' ' +
                  item.conditionValues[i][j]?.value?.dayType?.label
            }</span>`;
            break;

          case RulesConditions.Time:
            currentText = `${item.conditionText[i][j]} <span class="value">${item.conditionValues[i][j]?.operator?.label} ${
              !item.conditionValues[i][j]?.weekDays.length || item.conditionValues[i][j]?.weekDays.length === 7
                ? 'all week days'
                : item.conditionValues[i][j]?.weekDays
                    .slice()
                    .sort((a, b) => a.value - b.value)
                    .map((day) => day.label)
                    .join(', ')
            }</span> from <span class="value">${item.conditionValues[i][j]?.from.value}</span> to <span class="value">${
              item.conditionValues[i][j]?.to.value
            }</span>`;
            break;
          case RulesConditions.OrderFulfillmentStatus:
            currentText = `${item.conditionText[i][j]} <span class="value">${item.conditionValues[i][j]?.fulfillmentStatus?.label}</span>`;
            break;
          case RulesConditions.DeliveryStatus:
            currentText = `${item.conditionText[i][j]} ${item.conditionValues[i][j]?.deliveryStatus
              ?.map((status: any) => `<span class="value"> ${status.label} </span>`)
              .join(' or ')}`;
            break;
        }
        if (j != item.conditionNames[i].length - 1) {
          currentText += '<b> AND </b>';
        }
        text += currentText;
      }
      if (i != orWhenLength - 1) {
        text += '<br><b> OR </b>';
      }
    }

    return text;
  }

  getServiceLabelDisplayName(carrierName, serviceLevelType) {
    return carrierName + ' ' + ServiceLevelDisplayNames[serviceLevelType] || serviceLevelType;
  }

  getActionText(item: RuleItem) {
    let text = `<span class="value">${item.actionText}</span>`;
    let serviceLevel = '';
    let additionalText = '';
    switch (item.actionName) {
      case 'setInventoryLocation':
        text = text + '<span class="value" style="padding: 0 3px"> to </span>' + item.actionValue?.label;
        break;
      case 'unifyInventoryLocation':
        text = text + '<span class="value" style="padding: 0 3px"> to </span>' + item.actionValue?.label;
        break;
      case 'batchImmediatelyAndAssign':
        serviceLevel =
          item.actionValue.serviceLevel &&
          this.getServiceLabelDisplayName(item.actionValue.value, item.actionValue.serviceLevel.serviceLevel_type);
        additionalText = item.actionValue.batchName
          ? ` and name the batch <span class="value">${item.actionValue.batchName}</span>`
          : '';

        text = item.actionText.replace(
          '[[delivery service]]',
          `delivery service <span class="value">${serviceLevel || item.actionValue.value}</span> ${additionalText}`,
        );
        break;
      case RulesActions.SetWeightToSKU:
        text = `set weight <span class="value">${item.actionValue.weight}</span> ${this.shopSettings?.weightUnits || 'lb'} to SKU <span class="value">${item.actionValue.sku}</span>`;
        break;
      case 'batchOnSpecificTimeAndAssign':
        serviceLevel =
          item.actionValue.deliveryService.serviceLevel &&
          this.getServiceLabelDisplayName(
            item.actionValue.deliveryService.value,
            item.actionValue.deliveryService.serviceLevel.serviceLevel_type,
          );
        const upToText = `up to ${item.actionValue.batchUpToCount}`;
        const weekDaysText =
          !item.actionValue.weekDays.length || item.actionValue.weekDays.length === 7
            ? 'all week days'
            : item.actionValue.weekDays.join(', ');
        additionalText = item.actionValue.batchName
          ? ` and name the batch <span class="value">${item.actionValue.batchName}</span>`
          : '';
        text = `batch <span class="value">${
          item.actionValue.batchUpToMode !== 'all' ? upToText : 'all'
        }</span> order(s) and assign it to delivery service <span class="value">${
          serviceLevel || item.actionValue.deliveryService.value
        }</span> on <span class="value">${weekDaysText}</span> at <span class="value">${
          item.actionValue.atTime
        }</span> ${additionalText}`;
        break;

      case 'changeOrderStatusWithDelay':
        text = item.actionText.replace('[[X]]', `<span class="value">${item.actionValue.delayTime}</span>`);
        break;
      case 'setReturnPreferences':
        text = `Include a return label with the outbound shipment`;
        break;
      case 'setRecommendedCarrier':
        const shipOnText = item.actionValue.shipOn?.shipOn
          ? `${this.getRelativeDays('shipOn', item.actionValue.shipOn, 'Ship on')}.`
          : '';
        const shippingDays = item.actionValue.shipmentPreferncesFields?.maximalTransitTime;
        let str = '';
        const serviceLevelIds = item.actionValue.shipmentPreferncesFields?.serviceLevel?.map((sl) => sl.value);
        item.actionValue.shipmentPreferncesFields?.carrier?.forEach((carrier) => {
          if (str?.length) {
            str += ' and ';
          }
          str += `<span class="value">${carrier.label} </span>`;
          const serviceLevelsByCarrier = this.carrierOptions?.find((co) => co.value === carrier.value)?.serviceLevels;
          if (serviceLevelIds?.length && serviceLevelsByCarrier?.length) {
            const serviceLevels = serviceLevelsByCarrier
              ?.filter((serviceLevel) => serviceLevelIds.includes(serviceLevel.value))
              .map((sl) => sl.label)
              .join(',');
            str += `with <span class="value"> ${
              serviceLevels?.length ? ' [' + serviceLevels + '] </span> service level' : ' any </span> service level'
            }</span>`;
          }
        });
        if (shippingDays != null) {
          str += ` with <span class="value">${shippingDays}</span> max shipping days`;
        }
        text = `${item.actionText} ${shipOnText} ${str}`;
        break;
      case 'snoozeOrder':
        text = this.getRelativeDays('snoozeBy', item.actionValue, 'Snooze');
        break;
      case 'setShipmentExtras':
        const valuesObj = item.actionValue.valuesObj;
        const valuesStr = this.getShipmentExtrasValues(valuesObj);

        text = 'add the following shipment extras to the order <span class="value">' + valuesStr + '</span>';
        break;
      case RulesActions.TeamNote:
        let message = item.actionValue?.message;
        const mentions = item?.actionValue?.userCollaboration;
        mentions?.forEach((user) => {
          message = message.split('@' + user?.name).join(`<a class="user-mention">@${user?.name}</a>&nbsp;`);
        });
        text = `add team note with message <span class="value">${message}</span>`;
        break;
      case 'flagNotVerified':
        text = `<span>${item.actionText} <span class="value">${this.notVerifiedName}</span></span>`;
        break;
      case RulesActions.DeliveryMethod:
        text = `<span>${item.actionText} to <span class="value">${item.actionValue}</span></span>`;
        break;
      case RulesActions.ColorCoding:
        text = `<span>automatically ${item.actionValue.operator?.label} color coding to </span>`;
        break;
      case RulesActions.SplitOrderByStrategy:
        let detailsText = `<span class="value">${item.actionValue?.label}</span>`;
        let nameText, itemText;
        const isProductTagOrSkuListAction =
          item.actionValue?.value === 'product_tag' || item.actionValue?.value === 'sku_list';
        if (isProductTagOrSkuListAction) {
          if (item.actionValue?.value === 'product_tag') {
            nameText = 'product';
            itemText = 'tag(s)';
          } else if (item.actionValue?.value === 'sku_list') {
            nameText = 'sku list';
            itemText = 'sku(s)';
          } else {
            nameText = 'variant';
            itemText = 'sku(s)';
          }
        }
        if (
          isProductTagOrSkuListAction &&
          item.actionValue?.operator &&
          ['includes', 'excludes'].includes(item.actionValue?.operator2) &&
          item.actionValue?.productTags?.length
        ) {
          detailsText = `${nameText} that <span class="value">${item.actionValue.operator2} ${
            item.actionValue.operator
          } of</span> ${itemText} <span class="value">${item.actionValue.productTags
            .filter((val) => !!val?.name)
            .map((val) => val.name)
            .join(', ')}</span>`;
        }
        if (
          isProductTagOrSkuListAction &&
          item.actionValue?.operator &&
          ['contains', 'notContains'].includes(item.actionValue?.operator2) &&
          item.actionValue?.freeTextValue
        ) {
          detailsText = `${nameText} that <span class="value">${item.actionValue.operator2} ${item.actionValue.operator} of</span> ${itemText} <span class="value">${item.actionValue?.freeTextValue}</span>`;
        }

        text = `<span>split the order based on ${detailsText} so each is a separate order in PDQ</span>`;
        break;
      case RulesActions.AddTags:
      case RulesActions.DeleteTags:
        text = `<span>${item.actionText} <span class="value">${item.actionValue.join(', ')} </span></span>`;
        break;
      case RulesActions.SmartShipOn:
        const maxTransitTime = item.actionValue.shipmentPreferncesFields?.maximalTransitTime;
        let str2 = '';
        const carrierServiceLevelIds = item.actionValue.shipmentPreferncesFields?.serviceLevel?.map((sl) => sl.value);
        item.actionValue.shipmentPreferncesFields?.carrier?.forEach((carrier) => {
          if (str2?.length) {
            str2 += ' and ';
          }
          str2 += `<span class="value">${carrier.label} </span>`;
          const serviceLevelsByCarrier = this.carrierOptions?.find((co) => co.value === carrier.value)?.serviceLevels;
          if (carrierServiceLevelIds?.length && serviceLevelsByCarrier?.length) {
            const serviceLevels = serviceLevelsByCarrier
              ?.filter((serviceLevel) => carrierServiceLevelIds.includes(serviceLevel.value))
              .map((sl) => sl.label)
              .join(',');
            str2 += `with <span class="value"> ${
              serviceLevels?.length ? ' [' + serviceLevels + '] </span> service level' : ' any </span> service level'
            }</span>`;
          }
        });
        if (maxTransitTime != null) {
          str2 += ` with <span class="value">${maxTransitTime}</span> max shipping days`;
        }
        const rightOnTime = item.actionValue?.rightOnTime;
        const delayDays = item.actionValue.bufferDelayRisk?.delayDays;
        const optimiseForText = rightOnTime
          ? 'with optimise for <span class="value">right on time</span>'
          : `with optimise for <span class="value">zero delays</span> with days buffer of <span class="value">${delayDays}</span>`;

        text = `${item.actionText} ${str2} ${optimiseForText}`;
        break;
    }

    return text;
  }

  getRelativeDays(propertyName, actionValue, text) {
    const daysVal = actionValue.x;
    const productMetafieldKey = actionValue?.productMetafieldKey;
    const before = actionValue.before?.label;
    const data = actionValue[propertyName]?.label;
    return `${text} ${data
      ?.toString()
      ?.replace('[[x]]', '<span class="value">' + daysVal + '</span>')
      ?.replace('[[productMetafieldKey]]', '<span class="value">' + productMetafieldKey + '</span>')
      ?.concat(before ? ' <span class="value">' + before + '</span>' : '')}`;
  }

  getShipmentExtrasValues(valuesObj) {
    const valuesStr = valuesObj
      ?.filter((val) => val.value === true)
      ?.map((val) => {
        let ret = val.displayName;
        switch (val.fieldName) {
          case 'dryIce':
            const dryIceWeight = valuesObj.find((o) => o.fieldName === 'dryIceWeight');
            const dryIceWeightUnit = valuesObj.find((o) => o.fieldName === 'dryIceWeightUnit');

            ret = `${dryIceWeight?.value || 0} ${dryIceWeightUnit.value} Dry ice`;
            break;
          case 'signatureConfirmation':
            const signatureConfirmationValue = valuesObj.find((o) => o.fieldName === 'signatureConfirmationValue');
            const signatureValueStr = signatureConfirmationValue?.value?.toString()?.toLowerCase();
            ret = `Require ${capitalize(signatureValueStr)} signature on delivery`;
            break;
          case 'insurance':
            const insuranceAmount = valuesObj.find((o) => o.fieldName === 'insuranceAmount');
            const insuranceAmountType = valuesObj.find((o) => o.fieldName === 'insuranceAmountType');
            ret +=
              ` amount ` +
              (insuranceAmount?.value || 0) +
              `${InsuranceAmountTypeSign[insuranceAmountType?.value] ?? ''}`;
        }
        return ret;
      })
      ?.join(', ');
    return valuesStr;
  }
}
